import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = "https://www.vaughanmclean.com";

const SEO = ({ title, description, canonical, ogType = "website", jsonLd, noIndex = false }: SEOProps) => {
  const { pathname } = useLocation();
  const url = canonical || `${BASE_URL}${pathname}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (noIndex) {
      setMeta("robots", "noindex, follow");
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
    }
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", url, true);
    setMeta("og:type", ogType, true);
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);

    // JSON-LD
    const jsonLdId = "seo-jsonld";
    let script = document.getElementById(jsonLdId) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = jsonLdId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      // Cleanup JSON-LD on unmount
      document.getElementById(jsonLdId)?.remove();
      if (noIndex) document.querySelector('meta[name="robots"]')?.remove();
    };
  }, [title, description, url, ogType, jsonLd, noIndex]);

  return null;
};

export default SEO;

// Shared JSON-LD for the firm
export const FIRM_BASE_URL = BASE_URL;
export const firmJsonLd = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "Vaughan McLean",
  url: BASE_URL,
  telephone: "+1-215-569-2400",
  description:
    "Vaughan McLean provides practical, innovative and exceptional litigation and advisory services across the United States.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1635 Market Street, Suite 1600",
    addressLocality: "Philadelphia",
    addressRegion: "PA",
    postalCode: "19103",
    addressCountry: "US",
  },
  sameAs: [],
  areaServed: "United States",
  priceRange: "$$$$",
  logo: `${BASE_URL}/logo-15b.png`,
};
